import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Play, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { srcSetFor } from "@/lib/images";
import { EASE } from "@/components/common/Primitives";

/* ------------------------------------------------------------------ hooks */

/** Matches a media query and stays in sync */
export const useMediaQuery = (query) => {
  const [m, setM] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = (e) => setM(e.matches);
    setM(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, [query]);
  return m;
};

/**
 * Should we play background video at all? No when the visitor prefers reduced motion or has asked the
 * browser to save data – the poster photo is shown instead.
 */
export const useVideoAllowed = () => {
  const reduce = useReducedMotion();
  const [saveData, setSaveData] = useState(false);
  useEffect(() => {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c?.saveData) setSaveData(true);
  }, []);
  return !reduce && !saveData;
};

/* ------------------------------------------------------------------ video layer */

/**
 * Full-bleed background video with a photo poster underneath. The poster is painted first (LCP), the
 * video fades in the moment it is actually playing. Muted, inline, looping, paused in hidden tabs.
 * Falls back to the poster when autoplay is refused (low-power mode), when the user prefers reduced
 * motion or when Save-Data is on. `onVideo` hands the element up so the caller can draw loop progress.
 */
export const VideoLayer = ({ src, webm, poster, posterSrcSet, alt = "", position = "50% 50%", parallaxRef, parallax = 12, overscan = true, onVideo, testid = "hero-video" }) => {
  const reduce = useReducedMotion();
  const allowed = useVideoAllowed();
  const [ready, setReady] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: parallaxRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : `${parallax}%`]);

  useEffect(() => {
    const v = ref.current;
    if (!v || !allowed) return undefined;
    v.muted = true;
    v.defaultMuted = true;
    const onPlaying = () => setReady(true);
    const tryPlay = () => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
    const onVis = () => { if (document.hidden) v.pause(); else tryPlay(); };
    v.addEventListener("playing", onPlaying);
    document.addEventListener("visibilitychange", onVis);
    tryPlay();
    onVideo?.(v);
    return () => {
      v.removeEventListener("playing", onPlaying);
      document.removeEventListener("visibilitychange", onVis);
      onVideo?.(null);
    };
  }, [allowed, src]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div style={{ y }} className={`absolute inset-x-0 will-change-transform ${overscan && parallax ? "-top-[8%] h-[116%]" : "inset-y-0"}`} data-testid={testid} data-ready={ready ? "true" : "false"} data-mode={allowed ? "video" : "poster"}>
      <motion.div
        initial={reduce ? false : { scale: 1.06 }}
        animate={{ scale: 1 }}
        transition={{ duration: 5.5, ease: [0.16, 1, 0.3, 1] }}
        className="h-full w-full origin-center"
      >
        <img
          src={poster}
          srcSet={posterSrcSet}
          sizes={posterSrcSet ? "100vw" : undefined}
          alt={alt}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: position }}
          data-testid={`${testid}-poster`}
        />
        {allowed && (
          <video
            ref={ref}
            poster={poster}
            muted
            loop
            playsInline
            autoPlay
            preload="auto"
            disablePictureInPicture
            disableRemotePlayback
            aria-hidden="true"
            tabIndex={-1}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-out ${ready ? "opacity-100" : "opacity-0"}`}
            style={{ objectPosition: position }}
            data-testid={`${testid}-el`}
          >
            {/* MP4/H.264 first (Safari, iOS), then VP9 for browsers without H.264 */}
            {src && <source src={src} type="video/mp4" />}
            {webm && <source src={webm} type="video/webm" />}
          </video>
        )}
      </motion.div>
    </motion.div>
  );
};

/**
 * Thin line that fills as the hero loop plays – a quiet signal that the picture is live film, not a still.
 * Driven straight from the video element with requestAnimationFrame (no React re-renders).
 */
export const LoopProgress = ({ video, className = "", testid = "hero-loop-progress" }) => {
  const bar = useRef(null);
  useEffect(() => {
    if (!video || !bar.current) return undefined;
    let raf;
    const tick = () => {
      const d = video.duration || 0;
      const p = d ? Math.min(1, video.currentTime / d) : 0;
      if (bar.current) bar.current.style.transform = `scaleX(${p})`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [video]);
  return (
    <div className={`relative h-px w-full overflow-hidden bg-white/25 ${className}`} aria-hidden data-testid={testid}>
      <div ref={bar} className="absolute inset-0 origin-left bg-white will-change-transform" style={{ transform: "scaleX(0)" }} />
    </div>
  );
};

/** "Sjå filmen" – round play mark with label + running time */
export const PlayButton = ({ onClick, label, meta, size = "md", testid = "hero-play-button", className = "" }) => (
  <button type="button" onClick={onClick} className={`group/play inline-flex items-center gap-3.5 text-left text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${className}`} data-testid={testid} aria-label={`${label}${meta ? ` – ${meta}` : ""}`}>
    <span className={`flex shrink-0 items-center justify-center rounded-full border border-white/50 bg-white/10 backdrop-blur-md transition-[background-color,color,transform] duration-200 group-hover/play:bg-white group-hover/play:text-foreground group-active/play:scale-95 ${size === "sm" ? "h-11 w-11" : "h-14 w-14"}`}>
      <Play className={`${size === "sm" ? "h-4 w-4" : "h-5 w-5"} translate-x-px fill-current`} />
    </span>
    <span className="hero-shadow">
      <span className="block text-[0.9375rem] font-semibold leading-tight">{label}</span>
      {meta && <span className="mt-0.5 block text-sm text-white/70 tabular">{meta}</span>}
    </span>
  </button>
);

/** Lightbox with the full film and sound. The element is mounted only while open so audio stops on close. */
export const FilmDialog = ({ open, onOpenChange, src, poster, title, meta, closeLabel = "Lukk", testid = "film-dialog" }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="w-[min(1200px,96vw)] max-w-none gap-0 overflow-hidden border-0 bg-[hsl(214,40%,6%)] p-0 text-white shadow-pop sm:rounded-lg [&>button]:hidden" data-testid={testid}>
      <DialogTitle className="sr-only">{title}</DialogTitle>
      <DialogDescription className="sr-only">{meta}</DialogDescription>
      <div className="relative aspect-video w-full bg-black">
        {open && (
          <video
            src={src}
            poster={poster}
            controls
            autoPlay
            playsInline
            preload="metadata"
            className="h-full w-full"
            data-testid={`${testid}-video`}
          >
            <track kind="captions" />
          </video>
        )}
      </div>
      <div className="flex items-center justify-between gap-4 px-5 py-3.5">
        <div className="min-w-0">
          <p className="truncate text-[0.9375rem] font-semibold">{title}</p>
          {meta && <p className="mt-0.5 text-sm text-white/60 tabular">{meta}</p>}
        </div>
        <button type="button" onClick={() => onOpenChange(false)} className="inline-flex h-10 items-center gap-2 rounded-md border border-white/20 px-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70" data-testid={`${testid}-close`}>
          <X className="h-4 w-4" /> {closeLabel}
        </button>
      </div>
    </DialogContent>
  </Dialog>
);

/* ------------------------------------------------------------------ legacy photo slideshow (kept for other call sites) */

export const useSlideshow = (count, { interval = 7000, enabled = true } = {}) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (!enabled || count < 2) return undefined;
    const tick = () => { if (!document.hidden && !paused) setIndex((i) => (i + 1) % count); };
    const id = setInterval(tick, interval);
    return () => clearInterval(id);
  }, [count, interval, enabled, paused]);
  return { index, setIndex, paused, setPaused };
};

export const HeroSlides = ({ slides, index, position = "50% 55%", parallaxRef, reduce }) => {
  const { scrollYProgress } = useScroll({ target: parallaxRef, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "12%"]);
  return (
    <motion.div style={{ y: photoY }} className="absolute inset-x-0 -top-[8%] h-[116%] will-change-transform">
      {slides.map((s, i) => {
        const active = i === index;
        return (
          <motion.div key={s.src} initial={false} animate={{ opacity: active ? 1 : 0 }} transition={{ duration: reduce ? 0 : 1.6, ease: [0.4, 0, 0.2, 1] }} className="absolute inset-0" style={{ zIndex: active ? 2 : 1 }} aria-hidden={!active}>
            <motion.div key={`${s.src}-${active}`} initial={reduce ? false : { scale: 1.1 }} animate={{ scale: active ? 1 : 1.1 }} transition={{ duration: 9, ease: [0.16, 1, 0.3, 1] }} className="h-full w-full origin-center">
              <picture className="block h-full w-full">
                <source type="image/webp" srcSet={srcSetFor(s.src)} sizes="100vw" />
                <img src={s.src} alt={active ? s.alt : ""} fetchPriority={i === 0 ? "high" : "low"} loading={i === 0 ? "eager" : "lazy"} decoding="async" className="h-full w-full object-cover" style={{ objectPosition: s.position || position }} />
              </picture>
            </motion.div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export const SlideCounter = ({ slides, index, onSelect, interval = 7000, reduce, testid = "home-hero-slides" }) => (
  <div className="hero-shadow flex flex-col items-end gap-3 text-white" data-testid={testid}>
    <AnimatePresence mode="wait" initial={false}>
      <motion.p key={index} initial={reduce ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={reduce ? undefined : { opacity: 0, y: -6 }} transition={{ duration: 0.4, ease: EASE }} className="max-w-[30ch] text-right text-sm leading-snug text-white/85" data-testid={`${testid}-caption`}>
        {slides[index]?.caption}
      </motion.p>
    </AnimatePresence>
    <div className="flex items-center gap-3">
      <span className="text-xs font-semibold tabular text-white/80" data-testid={`${testid}-counter`}>
        {String(index + 1).padStart(2, "0")} <span className="text-white/45">/ {String(slides.length).padStart(2, "0")}</span>
      </span>
      <div className="flex items-center gap-1.5" role="tablist" aria-label="Slides">
        {slides.map((s, i) => (
          <button key={s.src} type="button" role="tab" aria-selected={i === index} aria-label={s.caption} onClick={() => onSelect(i)} data-testid={`${testid}-dot-${i}`} className="group relative h-6 w-9 focus-visible:outline-none">
            <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-white/35 transition-colors group-hover:bg-white/60" />
            {i === index && <motion.span key={`bar-${index}`} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: reduce ? 0 : interval / 1000, ease: "linear" }} className="absolute inset-x-0 top-1/2 h-px origin-left -translate-y-1/2 bg-white" />}
          </button>
        ))}
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ type + scroll helpers */

/** Headline where each word rises out of a clipping mask – one of those details that reads as crafted. Use "\n" in `text` to force a line break. */
export const WordReveal = ({ text, delay = 0, reduce, className = "", as: Tag = "h1", ...rest }) => {
  const lines = text.split("\n");
  let k = 0;
  return (
    <Tag className={className} {...rest}>
      {lines.map((line, li) => (
        <span key={`l-${li}`} className="block">
          {line.split(" ").map((w, i, arr) => {
            const idx = k++;
            return (
              <span key={`${w}-${idx}`} className="inline-block overflow-hidden pb-[0.08em] align-bottom">
                <motion.span
                  className="inline-block"
                  initial={reduce ? false : { y: "112%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  transition={{ duration: 0.9, delay: delay + idx * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  {w}
                </motion.span>
                {i < arr.length - 1 ? "\u00A0" : ""}
              </span>
            );
          })}
        </span>
      ))}
    </Tag>
  );
};

/** Expanding frame: the band starts slightly inset with soft corners and grows to full bleed as it enters the viewport. */
export const ExpandOnScroll = ({ children, className = "", radius = 36, inset = 0.94, testid }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "start 25%"] });
  const scale = useTransform(scrollYProgress, [0, 1], [reduce ? 1 : inset, 1]);
  const r = useTransform(scrollYProgress, [0, 1], [reduce ? 0 : radius, 0]);
  return (
    <div ref={ref} className={className} data-testid={testid}>
      <motion.div style={{ scale, borderRadius: r }} className="relative h-full w-full origin-center overflow-hidden will-change-transform">
        {children}
      </motion.div>
    </div>
  );
};
